// components/omore/OmoreAnalytics.tsx
// Ask Omore-specific interaction analytics

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { fetchOmoreAnalytics } from '../../services/omoreInteractionService';
import { exportAnalyticsToCSV, downloadCSV } from '../../services/omoreExportService';
import type { OmoreInteractionAggregatedAnalytics } from '../../types/OmoreInteractionTypes';
import './OmoreAnalytics.css';

const HELPER_TYPE_LABELS: Record<string, string> = {
    general: 'General Help',
    character: 'Character',
    setting: 'Setting',
    plot: 'Plot',
    dialogue: 'Dialogue',
    theme: 'Theme',
    worldBuilding: 'World Building',
    revision: 'Revision',
    brainstorm: 'Brainstorm',
};

const ORIGIN_LABELS: Record<string, string> = {
    home: 'Home',
    creatorTools: 'Creator Tools',
    storyCreator: 'Story Creator',
    characterBuilder: 'Character Builder',
    settingDesigner: 'Setting Designer',
    plotPlanner: 'Plot Planner',
};

const HELPER_COLORS: Record<string, string> = {
    general: '#667eea',
    character: '#ec4899',
    setting: '#10b981',
    plot: '#f59e0b',
    dialogue: '#8b5cf6',
    theme: '#06b6d4',
    worldBuilding: '#84cc16',
    revision: '#f97316',
    brainstorm: '#14b8a6',
};

const ORIGIN_COLORS: Record<string, string> = {
    home: '#667eea',
    creatorTools: '#10b981',
    storyCreator: '#ec4899',
    characterBuilder: '#f59e0b',
    settingDesigner: '#8b5cf6',
    plotPlanner: '#06b6d4',
};

export const OmoreAnalytics: React.FC = () => {
    const navigate = useNavigate();
    const { currentUser, signOut } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [analytics, setAnalytics] = useState<OmoreInteractionAggregatedAnalytics | null>(null);

    const [startDate, setStartDate] = useState<string>(() => {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        return d.toISOString().split('T')[0];
    });
    const [endDate, setEndDate] = useState<string>(new Date().toISOString().split('T')[0]);


    const loadAnalytics = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const data = await fetchOmoreAnalytics({ startDate, endDate });
            setAnalytics(data);
        } catch (err: any) {
            console.error('Error loading Omore analytics:', err);
            setError(err.message || 'Failed to load analytics');
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate]);

    useEffect(() => {
        loadAnalytics();
    }, [loadAnalytics]);

    const handleSignOut = async () => {
        try {
            await signOut();
            navigate('/login');
        } catch (error) {
            console.error('Failed to sign out:', error);
        }
    };

    // Derived data
    const helperEntries = analytics
        ? Object.entries(analytics.sessionsByHelperType).sort((a, b) => b[1] - a[1])
        : [];
    const helperMax = helperEntries.reduce((max, [, v]) => Math.max(max, v), 1);
    const helperTotal = helperEntries.reduce((s, [, v]) => s + v, 0);

    const originEntries = analytics
        ? Object.entries(analytics.sessionsByOrigin).sort((a, b) => b[1] - a[1])
        : [];
    const originMax = originEntries.reduce((max, [, v]) => Math.max(max, v), 1);

    // Daily sessions trend
    const sessionsTrend = analytics?.dailyStats.map(d => ({ date: d.date, value: d.totalSessions })) || [];
    const sessionsTrendMax = sessionsTrend.reduce((max, d) => Math.max(max, d.value), 1);

    // Suggestion adoption trend
    const adoptionTrend = analytics?.dailyStats.map(d => ({
        date: d.date,
        rate: d.totalSuggestionsShown > 0
            ? (d.totalSuggestionsAdopted / d.totalSuggestionsShown) * 100
            : 0,
    })) || [];
    const adoptionMax = adoptionTrend.reduce((max, d) => Math.max(max, d.rate), 1);

    // Avg session duration trend
    const durationTrend = analytics?.dailyStats.map(d => ({
        date: d.date,
        seconds: d.avgSessionDurationSeconds,
    })) || [];
    const durationMax = durationTrend.reduce((max, d) => Math.max(max, d.seconds), 1);

    return (
        <div className="omore-analytics-container">
            <header className="omore-analytics-header">
                <div className="header-content">
                    <div>
                        <h1>Ask Omore Analytics</h1>
                        <p className="header-subtitle">Interaction analytics for the Ask Omore helper</p>
                    </div>
                    <div className="header-actions">
                        <button onClick={() => navigate('/ai')} className="back-button">← AI Dashboard</button>
                        {analytics && (
                            <button
                                onClick={() => {
                                    const csv = exportAnalyticsToCSV(analytics);
                                    downloadCSV(csv, `omore-analytics-${Date.now()}.csv`);
                                }}
                                className="export-button"
                            >
                                Export CSV
                            </button>
                        )}
                        <span className="user-email">{currentUser?.email}</span>
                        <button onClick={handleSignOut} className="logout-button">Sign Out</button>
                    </div>
                </div>
            </header>

            <div className="omore-analytics-main">
                {/* Date filters */}
                <section className="filters-section">
                    <div className="filter-row">
                        <div className="filter-group">
                            <label>Start Date</label>
                            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
                        </div>
                        <div className="filter-group">
                            <label>End Date</label>
                            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
                        </div>
                    </div>
                </section>

                {loading && (
                    <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Loading Omore analytics...</p>
                    </div>
                )}

                {error && (
                    <div className="error-state">
                        <p>{error}</p>
                        <button onClick={loadAnalytics} className="retry-button">Retry</button>
                    </div>
                )}

                {!loading && !error && analytics && (
                    <>
                        {/* Summary cards */}
                        <div className="summary-cards">
                            <div className="summary-card">
                                <div className="summary-icon">💬</div>
                                <div className="summary-value">{analytics.totalSessions.toLocaleString()}</div>
                                <div className="summary-label">Total Sessions</div>
                            </div>
                            <div className="summary-card">
                                <div className="summary-icon">📝</div>
                                <div className="summary-value">{analytics.totalMessages.toLocaleString()}</div>
                                <div className="summary-label">Total Messages</div>
                            </div>
                            <div className="summary-card">
                                <div className="summary-icon">👤</div>
                                <div className="summary-value">{analytics.uniqueUsers.toLocaleString()}</div>
                                <div className="summary-label">Unique Users</div>
                            </div>
                            <div className="summary-card">
                                <div className="summary-icon">✅</div>
                                <div className="summary-value">{analytics.suggestionAdoptionRate.toFixed(1)}%</div>
                                <div className="summary-label">Suggestion Adoption</div>
                            </div>
                            <div className="summary-card">
                                <div className="summary-icon">⏱️</div>
                                <div className="summary-value">{(analytics.avgSessionDurationSeconds / 60).toFixed(1)}m</div>
                                <div className="summary-label">Avg Session Duration</div>
                            </div>
                        </div>

                        {/* Helper type breakdown + Origin breakdown side by side */}
                        <div className="two-col-charts">
                            <div className="chart-card">
                                <h3>Sessions by Helper Type</h3>
                                <div className="horiz-bar-list">
                                    {helperEntries.map(([type, count]) => {
                                        const pct = helperMax > 0 ? (count / helperMax) * 100 : 0;
                                        const color = HELPER_COLORS[type] || '#9ca3af';
                                        return (
                                            <div key={type} className="horiz-bar-item">
                                                <div className="horiz-bar-label">
                                                    <span>{HELPER_TYPE_LABELS[type] || type}</span>
                                                    <span className="horiz-bar-count">{count} ({helperTotal > 0 ? ((count / helperTotal) * 100).toFixed(1) : 0}%)</span>
                                                </div>
                                                <div className="horiz-bar-track">
                                                    <div className="horiz-bar-fill" style={{ width: `${pct}%`, background: color }}></div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="chart-card">
                                <h3>Sessions by Origin</h3>
                                <div className="horiz-bar-list">
                                    {originEntries.map(([origin, count]) => {
                                        const pct = originMax > 0 ? (count / originMax) * 100 : 0;
                                        const color = ORIGIN_COLORS[origin] || '#9ca3af';
                                        return (
                                            <div key={origin} className="horiz-bar-item">
                                                <div className="horiz-bar-label">
                                                    <span>{ORIGIN_LABELS[origin] || origin}</span>
                                                    <span className="horiz-bar-count">{count}</span>
                                                </div>
                                                <div className="horiz-bar-track">
                                                    <div className="horiz-bar-fill" style={{ width: `${pct}%`, background: color }}></div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* Daily sessions trend */}
                        <div className="chart-card">
                            <h3>Daily Sessions Over Time</h3>
                            <div className="bar-chart">
                                {sessionsTrend.map(point => {
                                    const h = sessionsTrendMax > 0 ? (point.value / sessionsTrendMax) * 100 : 0;
                                    return (
                                        <div key={point.date} className="bar-column">
                                            <span className="bar-value">{point.value}</span>
                                            <div className="bar-cell">
                                                <div className="bar-body" style={{ height: `${h}%` }}></div>
                                            </div>
                                            <span className="bar-date">{point.date.slice(5)}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Quick links to advanced analytics */}
                        <div className="two-col-charts">
                            <div className="chart-card">
                                <h3>Trend Analysis</h3>
                                <p style={{ color: '#718096', fontSize: '14px', margin: '8px 0 16px' }}>
                                    Compare week-over-week and month-over-month metrics
                                </p>
                                <button onClick={() => navigate('/omore/trends')} className="detail-link">
                                    View Trends →
                                </button>
                            </div>
                            <div className="chart-card">
                                <h3>Cohort Analysis</h3>
                                <p style={{ color: '#718096', fontSize: '14px', margin: '8px 0 16px' }}>
                                    Analyze user retention and engagement by cohort
                                </p>
                                <button onClick={() => navigate('/omore/cohorts')} className="detail-link">
                                    View Cohorts →
                                </button>
                            </div>
                        </div>

                        {/* Two trend charts: adoption rate + duration */}
                        <div className="two-col-charts">
                            <div className="chart-card">
                                <h3>Suggestion Adoption Rate (%)</h3>
                                <div className="bar-chart">
                                    {adoptionTrend.map(point => {
                                        const h = adoptionMax > 0 ? (point.rate / adoptionMax) * 100 : 0;
                                        return (
                                            <div key={point.date} className="bar-column">
                                                <span className="bar-value">{point.rate.toFixed(0)}%</span>
                                                <div className="bar-cell">
                                                    <div className="bar-body" style={{ height: `${h}%`, background: '#48bb78' }}></div>
                                                </div>
                                                <span className="bar-date">{point.date.slice(5)}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="chart-card">
                                <h3>Avg Session Duration (seconds)</h3>
                                <div className="bar-chart">
                                    {durationTrend.map(point => {
                                        const h = durationMax > 0 ? (point.seconds / durationMax) * 100 : 0;
                                        return (
                                            <div key={point.date} className="bar-column">
                                                <span className="bar-value">{point.seconds.toFixed(0)}s</span>
                                                <div className="bar-cell">
                                                    <div className="bar-body" style={{ height: `${h}%`, background: '#f59e0b' }}></div>
                                                </div>
                                                <span className="bar-date">{point.date.slice(5)}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
